import { enqueueSnackbar } from "notistack"
import { IDocHubFilters, IProjectMaterialDetailsResponse } from "pages/Projects/ProjectServices"
import { api } from "../../api"
import { setAlertFunc } from "../../components/AlertToaster/AlertSlice"
import {
    HistoryResponse,
    IDocHubDocument,
    IFile,
    IResponse,
    StatusButton,
    TicketDataResponseType,
} from "../Common/commonTypes"
import { getTicketDetails as getDemolitionTicketDetails } from "../Operations/features/Demolition/DemolitionSlice"
import { IFinanceOverviewResponse, PaymentTermsResponse, PurchaseOrderDetailsResponse } from "./FinanceTypes"

const MODULE_URL = `finance`

export interface IFinanceTabItem {
    status: string
    status_color: string
    status_id: number[]
    status_key: string
    status_text_color: string
    ticket_count: number
    total_pages: number
}
interface GetSkipStatusesParams {
    search_key?: string
    ticket_id: string
}

interface IParsedFields {
    amount_without_gst: number
    gst_amount: number
    gst_no: string
    credit_note_no: string
    credit_note_date: string
    invoice_date: string
    invoice_no: string
    irn_no: string
    purchase_no: string
    shipping_charges: number
    subtotal: number
    total_amount: number
    revenue_tagging_note: string
}
interface IExtractTaxInvoiceRes {
    low_confidence_flags: {
        amount_without_gst: boolean
        shipping_charges: boolean
    }
    message: string
    parsed_fields: IParsedFields
    s3_url: string
}

type FinanceDocHubFilters = Omit<IDocHubFilters, "project_id"> & {
    ticket_id: number
}

const handleSnackbar = async (
    queryFulfilled: any,
    successMsg: (data: any) => string,
    fallbackSuccess = "File Download Successfully",
    fallbackError = "Something went wrong"
) => {
    try {
        const { data } = await queryFulfilled
        enqueueSnackbar(successMsg(data) || fallbackSuccess, { variant: "success" })
    } catch (error: any) {
        enqueueSnackbar(error?.error?.data?.message || error?.error?.data?.error || error?.message || fallbackError, {
            variant: "error",
        })
    }
}

const financeService = api.injectEndpoints({
    endpoints: build => ({
        getFinanceTabs: build.query<IFinanceTabItem[], { data: any; selectedValue: number; page: number }>({
            query: params => ({
                url: `${MODULE_URL}/total`,
                params: {
                    search_key: params.data.search_key ?? "",
                    child_status_ids: JSON.stringify(params.data.child_status_ids ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    country_ids: JSON.stringify(params.data.country_ids ?? []),
                    zone_ids: JSON.stringify(params.data.zone_ids ?? []),
                    region_ids: JSON.stringify(params.data.region_ids ?? []),
                    state_ids: JSON.stringify(params.data.state_ids ?? []),
                    city_ids: JSON.stringify(params.data.city_ids ?? []),
                    site_ids: JSON.stringify(params.data.site_ids ?? []),
                    raised_on_from: params.data.raised_on_from ?? "",
                    raised_on_to: params.data.raised_on_to ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    payment_status: JSON.stringify(params.data.payment_status ?? []),
                    due_date_start: params.data.due_date_start ?? "",
                    due_date_end: params.data.due_date_end ?? "",
                    tag_ids: JSON.stringify(params.data.tag_ids ?? []),
                    category_ids: JSON.stringify(
                        (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    on_hold: params.data.on_hold ?? "",
                    on_hold_reason_ids: JSON.stringify(params.data.on_hold_reason_ids ?? []),
                    on_hold_date: params.data.on_hold_date ?? "",
                    product_ids: JSON.stringify(params.data.product_ids ?? []),
                    per_page: params?.selectedValue,
                    page: params?.page,
                    account_id: JSON.stringify(params.data.account_id ?? []),
                    parent_account_id: JSON.stringify(params.data.parent_account_id ?? []),
                    ecom_order_id: params.data.ecom_order_id ? JSON.stringify([params.data.ecom_order_id]) : JSON.stringify([]),
                },
                useV3: true,
            }),
            transformResponse: (response: any): IFinanceTabItem[] => response?.data?.message ?? [],
            providesTags: ["Finance"],
        }),
        getFinanceData: build.query<TicketDataResponseType, { data: any; selectedValue: number; page: number }>({
            query: params => ({
                url: `${MODULE_URL}/${params.data.parent_status ?? "all_tab"}`,
                params: {
                    search_key: params.data.search_key ?? "",
                    child_status_ids: JSON.stringify(params.data.child_status_ids ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    country_ids: JSON.stringify(params.data.country_ids ?? []),
                    zone_ids: JSON.stringify(params.data.zone_ids ?? []),
                    region_ids: JSON.stringify(params.data.region_ids ?? []),
                    state_ids: JSON.stringify(params.data.state_ids ?? []),
                    city_ids: JSON.stringify(params.data.city_ids ?? []),
                    site_ids: JSON.stringify(params.data.site_ids ?? []),
                    raised_on_from: params.data.raised_on_from ?? "",
                    raised_on_to: params.data.raised_on_to ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    payment_status: JSON.stringify(params.data.payment_status ?? []),
                    due_date_start: params.data.due_date_start ?? "",
                    due_date_end: params.data.due_date_end ?? "",
                    tag_ids: JSON.stringify(params.data.tag_ids ?? []),
                    category_ids: JSON.stringify(
                        (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    on_hold: params.data.on_hold ?? "",
                    on_hold_reason_ids: JSON.stringify(params.data.on_hold_reason_ids ?? []),
                    on_hold_date: params.data.on_hold_date ?? "",
                    product_ids: JSON.stringify(params.data.product_ids ?? []),
                    per_page: params?.selectedValue,
                    page: params?.page,
                    account_id: JSON.stringify(params.data.account_id ?? []),
                    parent_account_id: JSON.stringify(params.data.parent_account_id ?? []),
                    ecom_order_id: params.data.ecom_order_id ? JSON.stringify([params.data.ecom_order_id]) : JSON.stringify([]),
                },
                useV3: true,
            }),
            providesTags: ["Finance"],
        }),
        downloadFinance: build.query<IFile | { message: string; success: boolean }, { data: any }>({
            query: params => ({
                url: `${MODULE_URL}/download`,
                params: {
                    parent_status: params.data.parent_status ?? "all_tab",
                    search_key: params.data.search_key ?? "",
                    child_status_ids: JSON.stringify(params.data.child_status_ids ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    country_ids: JSON.stringify(params.data.country_ids ?? []),
                    zone_ids: JSON.stringify(params.data.zone_ids ?? []),
                    region_ids: JSON.stringify(params.data.region_ids ?? []),
                    state_ids: JSON.stringify(params.data.state_ids ?? []),
                    city_ids: JSON.stringify(params.data.city_ids ?? []),
                    site_ids: JSON.stringify(params.data.site_ids ?? []),
                    raised_on_from: params.data.raised_on_from ?? "",
                    raised_on_to: params.data.raised_on_to ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    payment_status: JSON.stringify(params.data.payment_status ?? []),
                    due_date_start: params.data.due_date_start ?? "",
                    due_date_end: params.data.due_date_end ?? "",
                    tag_ids: JSON.stringify(params.data.tag_ids ?? []),
                    category_ids: JSON.stringify(
                        (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    on_hold: params.data.on_hold ?? "",
                    on_hold_reason_ids: JSON.stringify(params.data.on_hold_reason_ids ?? []),
                    on_hold_date: params.data.on_hold_date ?? "",
                    product_ids: JSON.stringify(params.data.product_ids ?? []),
                },
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.message)
            },
            keepUnusedDataFor: 0,
        }),
        getFinaneOverviewDetails: build.query<
            IFinanceOverviewResponse,
            { ticket_id: string | number; account_type?: string }
        >({
            query: ({ ticket_id, account_type }) => ({
                url: `${MODULE_URL}/${ticket_id}/overview`,
                params: {
                    ...(account_type ? { account_type } : {}),
                },
                useV3: true,
            }),
            transformResponse: (response: any) => response?.message ?? {},
            providesTags: ["Finance"],
        }),
        getFinanceDocHub: build.query<IDocHubDocument[], FinanceDocHubFilters>({
            query: params => ({
                url: `${MODULE_URL}/${params.ticket_id}/doc_hub`,
                params,
                useV3: true,
            }),
            transformResponse: (response: { message: { data: IDocHubDocument[] } }) => {
                return response?.message?.data || []
            },
            providesTags: ["Finance"],
        }),
        getFinanceMaterialDetail: build.query<IProjectMaterialDetailsResponse, string>({
            query: ticket_id => ({
                url: `${MODULE_URL}/${ticket_id}/material_details`,
                useV3: true,
            }),
            transformResponse: (response: { message: IProjectMaterialDetailsResponse; success: boolean }) => {
                return response?.message
            },
            providesTags: ["Finance"],
        }),
        getPODDetails: build.query<any, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/get_pd_details`,
                params: {
                    ticket_id,
                },
            }),
        }),
        getFinancePurchaseOrders: build.query<PurchaseOrderDetailsResponse, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/po?po_type=receivable`,
                useV3: true,
            }),
            transformResponse: (response: any): PurchaseOrderDetailsResponse => response?.message ?? [],
        }),
        getTicketDetails: build.query<any, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/ticket_details`,
                useV3: true,
            }),
            transformResponse: (response: any) => response.message,
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getOtherDetails: build.query<IResponse<any>, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/other_details`,
                params: {
                    ticket_id,
                },
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getPaymentTermsData: build.query<any, { ticket_id: string; ticket_type?: string }>({
            query: params => ({
                url: `${MODULE_URL}/get_payment_terms`,
                params: {
                    ticket_id: params.ticket_id,
                    ticket_type: params?.ticket_type || "",
                },
                useV3: true
            }),
        }),
        getChargerFlag: build.query<any, { ticket_id: string; project_id: string }>({
            query: params => ({
                url: `${MODULE_URL}/approval_charger_modal`,
                params: {
                    ticket_id: params.ticket_id,
                    project_id: params?.project_id || "",
                },
            }),
        }),
        getSkipStatuses: build.query<IResponse<any>, GetSkipStatusesParams>({
            query: ({ search_key, ticket_id }) => ({
                url: `${MODULE_URL}/statuses`,
                params: {
                    search_key: search_key ?? "",
                },
                useV3: true,
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg?.ticket_id }],
        }),
        getTicketHistory: build.query<HistoryResponse[], { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/history`,
                useV3: true,
            }),
            transformResponse: (response: any): HistoryResponse[] => response?.message ?? [],
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getPaymentConditions: build.query<{ id: number; name: string }[], { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/conditions`,
                params: {
                    ticket_id,
                },
                useV3: true,
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getPaymentTime: build.query<HistoryResponse, { ticket_id: string }>({
            query: () => ({
                url: `${MODULE_URL}/time_uom`,
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        // for getting button dropdown options
        getFinanceButtonDropdown: build.query({
            query: (params: { ticket_id: string }) => ({
                url: `${MODULE_URL}/button`,
                params: params,
                useV3: true,
            }),
            transformResponse: (response: IResponse<{ message: StatusButton[] }>) => response.data.message,
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getChargerTemplate: build.mutation<any, { ticket_id: string; charger_details: any }>({
            query: body => ({
                url: `${MODULE_URL}/charger_operation`,
                method: "POST",
                useV3: true,
                body: body,
                responseHandler: (response: Response) => response.blob(),
                cache: "no-cache",
            }),
            async onQueryStarted(arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    const url = window.URL.createObjectURL(data)
                    const a = document.createElement("a")
                    a.href = url
                    a.download = `charger_template_${arg.ticket_id}.xlsx`
                    document.body.appendChild(a)
                    a.click()
                    window.URL.revokeObjectURL(url)
                    document.body.removeChild(a)
                } catch (error) {
                    console.error("Download failed:", error)
                }
            },
        }),
        getTaxInvoices: build.query<any, { id?: string; ticket_id?: string }>({
            query: params => ({
                url: `${MODULE_URL}/tax_invoice`,
                params,
                useV3: true,
            }),
            providesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        getPaymentTermsV3: build.query<PaymentTermsResponse, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/payment_terms`,
                useV3: true,
            }),
            transformResponse: (response: any): PaymentTermsResponse => response?.message ?? [],
            providesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        uploadBulkCharger: build.mutation<{ message: string }, { ticket_id: string; payload: FormData }>({
            query: body => ({
                url: `${MODULE_URL}/charger_operation`,
                method: "PUT",
                useV3: true,
                body: body.payload,
            }),
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        // for changing ticket status
        editFinanceTicketStatus: build.mutation<IResponse<unknown>, { ticket_id: string; body: FormData }>({
            query: ({ ticket_id, body }) => ({
                url: `${MODULE_URL}/${ticket_id}/update_status`,
                method: "PUT",
                useV3: true,
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.ticket_id },
                "Finance",
                "Logistics",
                "Installation",
            ],
        }),
        editSkipStatus: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/skip_to_status`,
                method: "PUT",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
            ],
        }),
        editTaxInvoice: build.mutation<{ msg: string }, FormData>({
            query: body => ({
                url: `${MODULE_URL}/tax_invoice`,
                method: "PUT",
                useV3: true,
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
            ],
        }),
        addTicketDetails: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket_details`,
                method: "POST",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
            ],
        }),
        addPaymentTerm: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/payment_term`,
                method: "POST",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
                "Projects",
                "Survey",
            ],
        }),
        addTransaction: build.mutation<IResponse<{ message: string }>, { data: FormData; module?: string }>({
            query: body => ({
                url: `${MODULE_URL}/transaction`,
                method: "POST",
                useV3: true,
                body: body.data,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    const urlParams = new URLSearchParams(window.location.search)
                    const ticketId = urlParams.get("ticket_id")
                    if (arg?.module === "demolitions") {
                        dispatch(getDemolitionTicketDetails(ticketId))
                    }
                    enqueueSnackbar(data.data.message, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error.message || "Something went wrong", { variant: "error" })
                }
            },
            invalidatesTags: (_, __, arg) => [
                { type: "Finance", id: arg.data.get("ticket_id") as string },
                "Finance",
                "Projects",
                "Survey",
            ],
        }),
        editTransaction: build.mutation<
            { msg: string },
            { data: FormData; view?: "table" | "details"; module?: string }
        >({
            query: body => ({
                url: `${MODULE_URL}/transaction`,
                method: "PUT",
                body: body.data,
                useV3: true
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.msg, { variant: "success" })

                    const urlParams = new URLSearchParams(window.location.search)
                    const ticketId = urlParams.get("ticket_id")
                    if (arg?.module === "demolitions") {
                        dispatch(getDemolitionTicketDetails(ticketId))
                    }
                } catch (error: any) {
                    enqueueSnackbar(error.message || "Something went wrong", { variant: "error" })
                }
            },
            invalidatesTags: (_, __, arg) => [
                { type: "Finance", id: arg.data.get("ticket_id") as string },
                "Finance",
                "Projects",
                "Survey",
            ],
        }),

        addTaxInvoice: build.mutation<{ msg: string }, { data: FormData; invalidateCache?: boolean }>({
            query: body => ({
                url: `${MODULE_URL}/tax_invoice`,
                method: "POST",
                useV3: true,
                body: body.data,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg.invalidateCache === false
                    ? []
                    : [{ type: "Finance", id: arg.data.get("ticket_id") as string }, "Finance"],
        }),
        VerifyTransaction: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/verify_transaction`,
                method: "POST",
                useV3: true,
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    const msg =
                        typeof data?.data === "string"
                            ? data.data
                            : ((data?.data as any)?.message ?? "Transaction verified")
                    dispatch(setAlertFunc({ type: "success", msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
            ],
        }),
        deleteTransaction: build.mutation<IResponse<any>, { data: any }>({
            query: ({ data }) => ({
                url: `${MODULE_URL}/transaction?ticket_id=${data?.ticket_id}&transaction_id=${data?.transaction_id}`,
                useV3: true,
                method: "DELETE"
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.data, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error.message || "Something went wrong!", { variant: "error" })
                }
            },
            invalidatesTags: (_, __, arg) => [
                { type: "Finance", id: arg?.data?.ticket_id as string },
                "Finance",
                "Projects",
                "Survey",
            ],
        }),
        deletePaymentTerm: build.mutation<IResponse<any>, { data: any }>({
            query: ({ data }) => ({
                url: `${MODULE_URL}/payment_term?ticket_id=${data?.ticket_id}&user_id=${data?.user_id}&payment_term_id=${data?.payment_term_id}`,
                method: "DELETE",
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg?.data?.ticket_id as string },
                "Finance",
            ],
        }),
        deleteTaxInvoice: build.mutation<{ msg: string }, { data: any }>({
            query: ({ data }) => ({
                url: `${MODULE_URL}/tax_invoice?ticket_id=${data?.ticket_id}&user_id=${data?.user_id}&id=${data?.id}&sent_on_type=${data?.sent_on_type ?? "invoice"}`,
                useV3: true,
                method: "DELETE",
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg?.data?.ticket_id as string },
                "Finance",
            ],
        }),
        addPaymentTermsInBulk: build.mutation<IResponse<any>, FormData>({
            query: data => ({
                url: `${MODULE_URL}/add_payment_terms_in_bulk`,
                method: "POST",
                body: data,
                useV3: true
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: arg?.get("ticket_id") as string }, "Finance"],
        }),
        extractTaxInvoice: build.mutation<IExtractTaxInvoiceRes, FormData>({
            query: data => ({
                url: `${MODULE_URL}/extract-tax-invoice`,
                method: "POST",
                body: data,
                useV3: true
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                } catch (error: any) {
                    enqueueSnackbar(error.error?.data?.error, { variant: "error" })
                }
            },
        }),
        saveTaxInvoiceData: build.mutation<{ msg: string }, FormData>({
            query: data => ({
                url: `${MODULE_URL}/tax-invoice`,
                method: "POST",
                body: data,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.error?.data?.error }))
                }
            },
        }),
        getFinanceLinkedTickets: build.query<any, { ticketId: number | string }>({
            query: ({ ticketId }) => ({
                url: `${MODULE_URL}/${ticketId}/tickets`,
                useV3: true,
            }),
            providesTags: ["Finance", "Logistics", "Projects"],
        }),
        unrejectFinanceTicket: build.mutation<{ message: string }, { ticketId: string | number }>({
            query: ({ ticketId }) => ({
                url: `${MODULE_URL}/${ticketId}/unreject`,
                useV3: true,
                method: "PUT",
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message)
                } catch (error: any) {
                    enqueueSnackbar(error.error?.data?.error, { variant: "error" })
                    dispatch(setAlertFunc({ type: "error", msg: error.error?.data?.error }))
                }
            },
            invalidatesTags: ["Projects", "Finance", "Logistics"],
        }),
    }),
})
export const {
    useLazyGetFinanceTabsQuery,
    useLazyGetFinanceDataQuery,
    useGetTicketDetailsQuery,
    useGetTicketHistoryQuery,
    useGetFinanceDocHubQuery,
    useGetFinanceMaterialDetailQuery,
    useGetFinanceButtonDropdownQuery,
    useGetPaymentTermsV3Query,
    useLazyGetPaymentTermsV3Query,
    useLazyGetPaymentTermsDataQuery,
    useLazyDownloadFinanceQuery,
    useLazyGetPODDetailsQuery,
    useGetSkipStatusesQuery,
    useGetChargerFlagQuery,
    useGetFinaneOverviewDetailsQuery,
    useGetOtherDetailsQuery,
    useGetPaymentConditionsQuery,
    useGetFinancePurchaseOrdersQuery,
    useLazyGetFinancePurchaseOrdersQuery,
    useGetPaymentTimeQuery,
    useGetTaxInvoicesQuery,
    useGetChargerTemplateMutation,
    useEditFinanceTicketStatusMutation,
    useEditSkipStatusMutation,
    useEditTaxInvoiceMutation,
    useVerifyTransactionMutation,
    useAddPaymentTermMutation,
    useAddTaxInvoiceMutation,
    useAddTicketDetailsMutation,
    useEditTransactionMutation,
    useAddTransactionMutation,
    useDeletePaymentTermMutation,
    useDeleteTaxInvoiceMutation,
    useDeleteTransactionMutation,
    useUploadBulkChargerMutation,
    useAddPaymentTermsInBulkMutation,
    useExtractTaxInvoiceMutation,
    useSaveTaxInvoiceDataMutation,
    useGetFinanceLinkedTicketsQuery,
    useUnrejectFinanceTicketMutation,
} = financeService

export { financeService }
